import { panelsOf, cellRect, visibleCells, activeCorners, isCorner } from "@/lib/geometry";
import { parseGlass } from "@/lib/sections";
import { glassOf } from "@/lib/glass";

const BITE = 14;

function union(intervals) {
  const s = [...intervals].sort((a, b) => a[0] - b[0]);
  return s.reduce((acc, [a, b]) => {
    const last = acc[acc.length - 1];
    if (last && a <= last[1]) last[1] = Math.max(last[1], b); else acc.push([a, b]);
    return acc;
  }, []);
}

export function computeAssembly(item) {
  const { width: W, height: H, frame_width: f, mullion_width: m, sash_width: s } = item;
  const panels = panelsOf(item);
  const sys = item.profile_system || "YKK AP";
  const list = [], tags = [];
  const add = (prefix, name, len, qty, cut, tagPts) => {
    let row = list.find((x) => x.name === name && x.len === Math.round(len));
    if (!row) {
      row = { mark: `${prefix}${list.filter((x) => x.mark.startsWith(prefix)).length + 1}`, name, len: Math.round(len), qty: 0, cut };
      list.push(row);
    }
    row.qty += qty;
    tagPts.forEach(([x, y]) => tags.push({ x, y, mark: row.mark }));
  };
  const corners = activeCorners(item), fx = [0, ...corners.map((k) => k.x), W];
  const endCut = (k) => (!k ? "45° mitre" : k.type === "post" ? "butt to corner post" : `${(180 - k.angle) / 2}° corner mitre`);
  fx.slice(1).forEach((x1, i) => {
    const x0 = fx[i], kL = corners[i - 1], kR = corners[i];
    const len = x1 - x0 - (kL?.type === "post" ? m / 2 : 0) - (kR?.type === "post" ? m / 2 : 0);
    const a = endCut(kL), b = endCut(kR), cut = a === b ? a : `${a} / ${b}`;
    add("F", "Outer frame – head", len, 1, cut, [[(x0 + x1) / 2, f / 2]]);
    add("F", "Outer frame – sill", len, 1, cut, [[(x0 + x1) / 2, H - f / 2]]);
  });
  add("F", "Outer frame – jamb", H, 2, "45° mitre", [[f / 2, H / 2], [W - f / 2, H / 2]]);

  const vx = [...new Set(panels.map((p) => p.x0).filter((x) => x > 0 && x < W))];
  const mullionRuns = {};
  let silicone = 0;
  vx.forEach((x) => {
    const k = corners.find((q) => q.x === x);
    mullionRuns[x] = union(panels.filter((p) => p.x0 === x).map((p) => [p.y0, p.y1]));
    mullionRuns[x].forEach(([a, b]) => {
      const len = b - a - (a === 0 ? f : m / 2) - (b === H ? f : m / 2);
      if (k?.type === "glass") silicone += len;
      else if (k) add("C", `Corner post ${k.angle}°`, len, 1, "90° butt", [[x, (a + b) / 2]]);
      else add("M", "Mullion", len, 1, "90° butt", [[x, (a + b) / 2]]);
    });
  });
  Object.entries(item._couple || {}).filter(([, k]) => k.own).forEach(([side, k]) => {
    add("C", isCorner(k) ? `Corner coupling post ${k.angle}°` : "Coupling mullion", H, 1, `90° butt · to ${k.tag}`, [[side === "left" ? 0 : W, H * 0.62]]);
  });
  const hy = [...new Set(panels.map((p) => p.y0).filter((y) => y > 0 && y < H))];
  hy.forEach((y) => {
    union(panels.filter((p) => p.y0 === y).map((p) => [p.x0, p.x1])).forEach(([a, b]) => {
      const stops = vx.filter((x) => x > a && x < b && mullionRuns[x].some(([p, q]) => p < y && q > y)).sort((p, q) => p - q);
      [a, ...stops, b].slice(1).forEach((e, i, arr) => {
        const st = i === 0 ? a : arr[i - 1];
        const len = e - st - (st === 0 ? f : m / 2) - (e === W ? f : m / 2);
        add("T", "Transom", len, 1, "90° butt", [[(st + e) / 2, y]]);
      });
    });
  });

  const glassRows = [];
  let gasket = 0, bulb = 0, cleats = 4, sashCount = 0;
  visibleCells(item).forEach(({ r, c }) => {
    const op = item.cells[r][c];
    const cr = cellRect(item, r, c);
    let gw = cr.w, gh = cr.h;
    if (op !== "fixed" && op !== "louvre") {
      sashCount++;
      add("S", op.startsWith("door") ? "Door leaf – rail" : "Sash – rail", cr.w, 2, "45° mitre", [[cr.x + cr.w / 2, cr.y + Math.min(s, cr.h / 4) / 2]]);
      add("S", op.startsWith("door") ? "Door leaf – stile" : "Sash – stile", cr.h, 2, "45° mitre", []);
      const ss = Math.min(s, cr.w / 4, cr.h / 4);
      gw -= 2 * ss; gh -= 2 * ss;
      bulb += 2 * (cr.w + cr.h) * 2;
      cleats += 4;
    }
    const spec = (op === "louvre" ? "Louvre blades" : glassOf(item, r, c)) || "—";
    const gsz = [Math.round(gw + 2 * BITE), Math.round(gh + 2 * BITE)];
    const existing = glassRows.find((g) => g.w === gsz[0] && g.h === gsz[1] && g.spec === spec);
    if (existing) existing.qty++; else glassRows.push({ mark: `G${glassRows.length + 1}`, w: gsz[0], h: gsz[1], spec, qty: 1, t: parseGlass(spec).T });
    tags.push({ x: cr.x + cr.w / 2, y: cr.y + cr.h / 2, mark: glassRows.find((g) => g.w === gsz[0] && g.h === gsz[1] && g.spec === spec).mark, glass: true });
    gasket += 2 * (gw + gh) * 2;
  });
  const sides = [W, W, H, H];
  const anchors = sides.reduce((a, L) => a + Math.max(2, Math.ceil((L - 300) / 500) + 1), 0);
  const accessories = [
    ["EPDM wedge + glazing gaskets", `${(gasket / 1000).toFixed(1)} m`],
    ...(bulb ? [["EPDM bulb / weather seals", `${(bulb / 1000).toFixed(1)} m`]] : []),
    ["Corner cleats (crimped / pinned)", `${cleats} nos`],
    ["Shear blocks for mullion/transom", `${(list.filter((x) => /Mullion|Transom|Corner post/.test(x.name)).reduce((a, x) => a + x.qty, 0)) * 2} nos`],
    ...(silicone ? [["Structural silicone glass-to-glass corner joint", `${(silicone / 1000).toFixed(1)} m`]] : []),
    ["Anchor fixings Ø6 SS + plug", `${anchors} nos`],
    ["Perimeter silicone + backer rod", `${((2 * (W + H) * 2) / 1000).toFixed(1)} m`],
    ...(sashCount ? [["Hardware sets (hinges/stays/locks)", `${sashCount} sets`]] : []),
  ];
  return { members: list.map((x) => ({ ...x, profile: `${sys}` })), glass: glassRows, accessories, tags };
}
